import { SelectionManager } from 'utils/selectionManager';
import dayjs from 'dayjs';
import { COMPANY_STATUSES } from './constants';

const { generateURLWithParams } = require('utils');
const { captureException } = require('utils/errors');
const { default: http } = require('utils/http');
const { LocalJsonStorage } = require('utils/storage');

const QUERY_KEY = 'companies-list';

// Create a selection manager instance for companies
const companySelectionManager = new SelectionManager('companies-selection');

export class CompanyList {
  static async fetch(queryParams) {
    return http.get(generateURLWithParams(
      'v1/organizations/{{organizationId}}/companies/',
      queryParams,
    )).then(({ data: { results: data = [], count: total } }) => {
      const { page, page_size, ordering } = queryParams;

      if (CompanyList.page_size !== page_size || CompanyList.ordering !== ordering) {
        CompanyList.update({
          pages: {
            [page]: data.map(({ id }) => id),
          },
        });
      } else {
        CompanyList.update({
          pages: {
            ...CompanyList.get().pages,
            [page]: data.map(({ id }) => id),
          },
        });
      }
      CompanyList.update({
        page_size,
        ordering,
        count: total,
      });
      return {
        page,
        data,
        total,
      };
    })
      .catch(captureException);
  }

  static get ordering() {
    return LocalJsonStorage.get(QUERY_KEY)?.ordering || '-status_order,-last_activity,-id';
  }

  static get page_size() {
    return LocalJsonStorage.get(QUERY_KEY)?.page_size || 20;
  }

  static get count() {
    return LocalJsonStorage.get(QUERY_KEY)?.count || 0;
  }

  static get hasNext() {
    const { pages } = CompanyList.get();

    // If no pages cached, assume there might be more
    if (!pages || Object.keys(pages).length === 0) {
      return true;
    }

    // Get the highest page number that was fetched
    const pageNumbers = Object.keys(pages).map(Number);
    const lastPageNumber = Math.max(...pageNumbers);
    const lastPageData = pages[lastPageNumber];

    // If the last fetched page is empty, there are no more pages
    if (lastPageData && lastPageData.length === 0) {
      return false;
    }

    // If the last page has fewer items than page_size, it's likely the last page
    if (lastPageData && lastPageData.length < CompanyList.page_size) {
      return false;
    }

    // Otherwise, assume there might be more pages
    return true;
  }

  static update(payload) {
    LocalJsonStorage.set({
      key: QUERY_KEY,
      value: {
        ...CompanyList.get(),
        ...payload,
      },
    });
  }

  static get() {
    return LocalJsonStorage.get(QUERY_KEY) || {
      ordering: '-status_order,-last_activity,-id',
      page_size: 20,
      pages: {},
    };
  }

  // Selection management methods using generic SelectionManager
  static getSelection() {
    return companySelectionManager.getSelection();
  }

  static clearSelection() {
    companySelectionManager.clearSelection();
  }

  static setSelectAll(selectAll) {
    companySelectionManager.setSelectAll(selectAll);
  }

  static isSelected(companyId) {
    return companySelectionManager.isSelected(companyId);
  }

  static toggleSelection(companyId) {
    companySelectionManager.toggleSelection(companyId);
  }

  static getSelectedIds() {
    return companySelectionManager.getSelectedIds();
  }

  static getSelectedCount() {
    return companySelectionManager.getSelectedCount(CompanyList.count);
  }

  static getPageSelectedIds(pageCompanies) {
    return companySelectionManager.getPageSelectedIds(pageCompanies);
  }

  static isPageFullySelected(pageCompanies) {
    return companySelectionManager.isPageFullySelected(pageCompanies);
  }

  static isPagePartiallySelected(pageCompanies) {
    return companySelectionManager.isPagePartiallySelected(pageCompanies);
  }

  static async getNext(id) {
    const { pages } = CompanyList.get();
    const list = Object.entries(pages).find(([page, ids]) => ids.includes(+id));
    if (list) {
      const [page, ids] = list;
      const index = ids.indexOf(+id);
      const nextId = ids[index + 1];
      if (nextId) {
        return nextId;
      }
      const nextPage = +page + 1;
      if (pages[nextPage]?.length) {
        return pages[nextPage][0];
      }
    }
    if (!CompanyList.hasNext) {
      return undefined;
    }
    // Find the next page number correctly
    const pageNumbers = Object.keys(pages).map(Number);
    const nextPage = pageNumbers.length > 0 ? Math.max(...pageNumbers) + 1 : 1;
    const nextIds = (await CompanyList.fetch({
      ordering: CompanyList.ordering,
      page: nextPage,
      page_size: CompanyList.page_size,
    }).then((res) => (res && res.data ? res.data.map((c) => c.id) : [])));
    LocalJsonStorage.set({
      key: QUERY_KEY,
      value: {
        ...CompanyList.get(),
        pages: {
          ...pages,
          [nextPage]: nextIds,
        },
      },
    });
    if (!nextIds || nextIds.length === 0) {
      return undefined;
    }
    return nextIds[0];
  }

  static async getPrevId(id) {
    const { pages } = CompanyList.get();
    const idNum = +id;
    // Sort pages numerically
    const entries = Object.entries(pages)
      .map(([page, ids]) => [Number(page), ids])
      .sort((a, b) => a[0] - b[0]);
    const listIndex = entries.findIndex(([_, ids]) => ids.includes(idNum));
    if (listIndex !== -1) {
      const [page, ids] = entries[listIndex];
      const index = ids.indexOf(idNum);
      if (index > 0) {
        const prevId = ids[index - 1];
        return prevId;
      }
      if (listIndex > 0) {
        // Get last id from previous cached page
        const [prevPage, prevIds] = entries[listIndex - 1];
        const prevId = prevIds[prevIds.length - 1];
        return prevId;
      }
      // This is the first page in cache, check if there is a previous page (not cached)
      const minPage = entries.length > 0 ? entries[0][0] : 1;
      const prevPage = minPage - 1;
      if (prevPage >= 1) {
        const prevIds = (await CompanyList.fetch({
          ordering: CompanyList.ordering,
          page: prevPage,
          page_size: CompanyList.page_size,
        }).then((res) => (res && res.data ? res.data.map((c) => c.id) : [])));
        LocalJsonStorage.set({
          key: QUERY_KEY,
          value: {
            ...CompanyList.get(),
            pages: {
              ...pages,
              [prevPage]: prevIds,
            },
          },
        });
        if (prevIds.length > 0) {
          const prevId = prevIds[prevIds.length - 1];
          return prevId;
        }
      }
    }
    return undefined;
  }
}

export const getStatusTagColor = (status) => {
  const statusObj = COMPANY_STATUSES[status?.toUpperCase().split(' ').join('_')];
  if (!statusObj) {
    return null;
  }
  return statusObj.color;
};

export const COMPANY_STATUS_OPTIONS = Object.values(COMPANY_STATUSES).map(({ label }) => ({
  value: label,
  label,
}));

export const serializeFilters = (filters) => {
  const serializedFilters = {
    search: filters.search,
    status: filters.status,
    source: filters.source,
    sic_category: filters.sicCategory,
    owner: filters.owner?.value,
    market: filters.market?.value,
    has_order_from_merchant: filters.order_from?.value,
  };
  if (filters.client_type) {
    serializedFilters.is_corporate = filters.client_type === 'corporate' ? 'True' : 'False';
  }
  if (filters.created_date) {
    serializedFilters.created_date = dayjs(filters.created_date).format('YYYY-MM-DD');
  }
  if (filters.workflow_status) {
    serializedFilters.has_active_workflow = filters.workflow_status === 'Enrolled' ? 'True' : 'False';
  }
  if (filters.order_date) {
    serializedFilters.has_order_between = filters.order_date.range.map((date) => dayjs(date).format('YYYY-MM-DD')).join(',');
  }
  return serializedFilters;
};
